<template lang="pug">
  div(:class="['widget',\
             { 'border-error': error,\
               'custom-container': true } ]")

    div(v-if="!creating && (loadingSites || loadingFrs)"
        id="loading")
      v-progress-linear(class="ma-0"
                        color="white"
                        indeterminate
                        height="2")
    div(v-if="creating" id="creating")
      v-layout(align-center justify-center fill-height)
        v-progress-circular(size="50" indeterminate)

    div(v-show="!creating" id="map" ref="map")
      v-layout(align-center justify-center fill-height)
        | A map should come here... An error must have happened.

    div(v-if="!creating" id="legend")
      map-legend

    template(v-if="franchises !== null")
      template(v-for="(fr, index) in franchises")
        div(:id="'franchise' + index" :key="index" style="display: none;")
          span(class="title")
            router-link(:to="{ name: 'franchise', params: { id: fr.id } }"
                        class="clickable")
              | {{ fr.name }}

    template(v-if="sites !== null")
      template(v-for="(site, index) in sites.slice(0, 11)")
        div(:id="'site' + index" :key="site.id" style="display: none;")
          span(class="title")
            router-link(:to="{ name: 'site', params: { id: site.id } }"
                        class="clickable")
              | {{ site.id }}

</template>

<script>
import loadGoogleMapsAPI from "./Map/loadGoogleMapsAPI.js";
import { style } from "./Map/style.js";
import { SITE, HARDWARE_STORE } from "./Map/icons.js";

import MapLegend from "./Map/Legend.vue";

export default {
  name: "custom-map",
  components: { MapLegend },
  data () {
    return {
      franchises: null,
      creating: true,
      error: false,
      loadingFrs: false,
      loadingSites: false,
      map: null,
      sitePosition: null,
      sites: null,
      sitesMarkers: [],
      test: false
    };
  },
  created () {
    this.$http.
      get(`/api/sites/${this.$route.params.id}/position`).
      then((position) => {
        this.sitePosition = Object.assign({}, position.data);
        this.createMap();
      }).
      catch(() => this.error = true).
      finally(() => this.creating = false);

    this.loadingFrs = true;
    this.$http.
      get("/api/franchises").
      then((franchises) => {
        this.franchises = Array.from(franchises.data);
        // Make sure view has been updated to select by id
        this.$nextTick().then(() => this.addFrsMarkers());
      }).
      catch(() => this.error = true).
      finally(() => this.loadingFrs = false);

    this.loadingSites = true;
    this.$http.
      get(`/api/sites?center=${this.$route.params.id}`).
      then((sites) => {
        this.sites = Array.from(sites.data);
        // Make sure view has been updated to select by id
        this.$nextTick().then(() => this.addSitesMarkers());
      }).
      catch(() => this.error = true).
      finally(() => this.loadingSites = false);
  },
  mounted () {
    /*
     * Defines window.google.maps
     * This is in mounted because createMap() needs this.$refs
     */
    loadGoogleMapsAPI().
      then(() => this.createMap());
  },
  methods: {
    addFrsMarkers () {
      if (this.map === null || this.franchises === null) {
        return;
      }
      for (let i = 0; i < this.franchises.length; i++) {
        if (this.franchises[i].lat && this.franchises[i].lng) {
          const marker = new window.google.maps.Marker({
            icon: {
              anchor: new window.google.maps.Point(15, 10),
              fillColor: "#90caf9",
              fillOpacity: 1,
              path: HARDWARE_STORE,
              scale: 0.6
            },
            position: {
              lat: this.franchises[i].lat,
              lng: this.franchises[i].lng
            },
            map: this.map
          });
          const infoContent = document.getElementById(`franchise${i}`);
          const infoWin = new window.google.maps.InfoWindow(
            { content: infoContent }
          );
          marker.addListener("click", () => {
            infoWin.open(
              this.map, marker
            );
            infoContent.style.display = "initial";
          });
        }
      }
    },
    addSiteMarker () {
      new window.google.maps.Marker({
        clickable: false,
        icon: {
          anchor: new window.google.maps.Point(10, 50),
          fillColor: "#8d6e63",
          fillOpacity: 1,
          path: SITE,
          scale: 0.6
        },
        map: this.map,
        position: this.sitePosition
      });
    },
    addSitesMarkers () {
      if (this.map === null || this.sites === null) {
        return;
      }
      this.sitesMarkers.push(undefined);
      for (let i = 1; i < this.sites.length && i <= 10; i += 1) {
        if (this.sites[i].issues > 0) {
          this.sitesMarkers.push(new window.google.maps.Marker({
            icon: {
              anchor: new window.google.maps.Point(10, 50),
              fillColor: "#e53935",
              fillOpacity: 1,
              path: SITE,
              scale: 0.6
            },
            position: {
              lat: this.sites[i].lat,
              lng: this.sites[i].lng
            },
            map: this.map
          }));
        } else {
          this.sitesMarkers.push(new window.google.maps.Marker({
            icon: {
              anchor: new window.google.maps.Point(10, 50),
              fillColor: "#4caf50",
              fillOpacity: 1,
              path: SITE,
              scale: 0.6
            },
            position: {
              lat: this.sites[i].lat,
              lng: this.sites[i].lng
            },
            map: this.map
          }));
        }
        const infoContent = document.getElementById(`site${i}`);
        const infoWin = new window.google.maps.InfoWindow(
          { content: infoContent }
        );
        this.sitesMarkers[i].addListener("click", () => {
          infoWin.open(
            this.map, this.sitesMarkers[i]
          );
          infoContent.style.display = "initial";
        });
      }
    },
    createMap () {
      if (this.sitePosition === null) {
        return;
      }
      if (!window.google) {
        return;
      }
      this.map = new window.google.maps.Map(
        this.$refs.map,
        {
          center: this.sitePosition,
          gestureHandling: "greedy",
          mapTypeControlOptions: { mapTypeIds: [] },
          mapTypeId: "terrain",
          scaleControl: true,
          streetViewControlOptions:
            { position: window.google.maps.ControlPosition.BOTTOM_LEFT },
          styles: style,
          zoom: 11,
          zoomControl: false
        });
      this.creating = false;
      this.addSiteMarker();
      // Make sure view has been updated to select by id
      this.$nextTick().then(() => {
        this.addFrsMarkers();
        this.addSitesMarkers();
      });
    }
  }
};
</script>

<style scoped>
.custom-container {
  display: flex;
  flex-direction: column;
}
#loading { flex: 0 1 auto; }
#creating { flex: 1 1 auto; }
#map { flex: 1 1 auto; }
#legend { flex: 0 1 auto; }
</style>

<style>
a[href^="https://maps.google.com/maps"] { display:none !important; }
div.gm-style-cc a { display:none !important; }
div.gmnoprint > .gm-style-cc { display:none !important; }
div.gmnoprint.gm-style-cc { display:none !important; }
div.gm-iv-address-link { display:none !important; }
div.gm-iv-vertical-separator { display:none !important; }
div.gm-iv-marker { display:none !important; }
</style>



// WEBPACK FOOTER //
// src/components/Site/Map.vue